import { Controller } from "@hotwired/stimulus"

// SEC25-750: stored credentials are never rendered into the page. When the
// field is empty (a secret is saved), the eye control fetches the decrypted
// value from an authenticated, org-scoped endpoint on demand; otherwise it
// just toggles the visibility of whatever the admin has typed. Either way it
// stays out of the page source until requested.
export default class extends Controller {
  static targets = ["input", "button"]
  static values = { url: String }

  toggle() {
    if (this.revealed) {
      this.mask()
    } else {
      this.reveal()
    }
  }

  async reveal() {
    // If the field already holds a value — typed by the admin (e.g. rotating
    // the credential) or fetched on a previous reveal — just show it. Never
    // fetch over a non-empty field; that would clobber the admin's input.
    if (this.inputTarget.value !== "") {
      this.show()
      return
    }
    // Guard against overlapping fetches from rapid double-clicks.
    if (this.loading) return

    this.loading = true
    this.setIcon("fa-spinner fa-spin")
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        // An expired session 302s to the login page; fail loudly instead of
        // parsing the login HTML as though it were the secret.
        redirect: "error",
      })
      const contentType = response.headers.get("Content-Type") || ""
      if (!response.ok || !contentType.includes("application/json")) {
        throw new Error(`Unexpected response ${response.status}`)
      }

      const data = await response.json()
      this.inputTarget.value = data.value
      this.show()
    } catch (_error) {
      this.setIcon("fa-exclamation-circle")
      this.buttonTarget.title = "Couldn't load the secret — reload the page and try again."
    } finally {
      this.loading = false
    }
  }

  show() {
    this.inputTarget.type = "text"
    this.revealed = true
    this.setIcon("fa-eye-slash")
  }

  mask() {
    // Hide only; keep the field's value so typed input isn't lost.
    this.inputTarget.type = "password"
    this.revealed = false
    this.setIcon("fa-eye")
  }

  setIcon(className) {
    const icon = this.buttonTarget.querySelector("i")
    if (icon) icon.className = `fas ${className} toggle-password`
  }
}
